import * as React from "react"

/**
 * One glyph per kind of thing.
 *
 * **Drawn, not typed.** Emoji would have been three characters of work and would render as
 * a different picture on every platform, at a size nobody controls, in a colour that
 * ignores the theme. These are strokes in `currentColor`, so they inherit the text colour
 * in both themes and stay the same drawing everywhere.
 *
 * The set is deliberately literal — a bug is an insect, a table is a grid, a board is
 * columns — because an icon that needs explaining is a worse label than the word it
 * replaced. Every icon here sits *beside* its word rather than instead of it.
 */
const PATHS: Record<string, React.ReactNode> = {
  // Places
  folder: <path d="M2 4.5A1.5 1.5 0 0 1 3.5 3h3l1.5 2h4.5A1.5 1.5 0 0 1 14 6.5v5A1.5 1.5 0 0 1 12.5 13h-9A1.5 1.5 0 0 1 2 11.5z" />,
  board: <><rect x="2" y="3" width="3.5" height="10" rx="1" /><rect x="6.25" y="3" width="3.5" height="7" rx="1" /><rect x="10.5" y="3" width="3.5" height="9" rx="1" /></>,
  document: <><path d="M4 2h5l3 3v9H4z" /><path d="M9 2v3h3" /></>,
  table: <><rect x="2" y="3.5" width="12" height="9" rx="1" /><path d="M2 6.5h12M6 6.5v6" /></>,
  registry: <><rect x="2" y="3.5" width="12" height="9" rx="1" /><path d="M2 6.5h12M2 9.5h12" /></>,

  // Work
  epic: <><path d="M8 2.5 13 5v6l-5 2.5L3 11V5z" /><path d="M8 6.5 10.5 7.75v2.5L8 11.5 5.5 10.25v-2.5z" /></>,
  feature: <path d="M8 2.2l1.7 3.9 4.1.4-3.1 2.8.9 4.1L8 11.3l-3.6 2.1.9-4.1-3.1-2.8 4.1-.4z" />,
  story: <><path d="M3 3.5h10v9H3z" /><path d="M5.5 6.5h5M5.5 9.5h3" /></>,
  task: <><rect x="2.5" y="3" width="11" height="10" rx="1.5" /><path d="M5.5 8.2l1.8 1.8 3.4-3.6" /></>,
  bug: <><path d="M5.5 6.5a2.5 2.5 0 0 1 5 0v3a2.5 2.5 0 0 1-5 0z" /><path d="M5.5 8H3M13 8h-2.5M6 4.5 4.8 3.2M10 4.5l1.2-1.3M6 11.5l-1.2 1.3M10 11.5l1.2 1.3" /></>,
  idea: <><path d="M6 12.5h4M6.5 14h3" /><path d="M5 6.7a3 3 0 1 1 6 0c0 1.3-.8 2-1.3 2.8-.3.5-.3 1-.3 1H6.6s0-.5-.3-1C5.8 8.7 5 8 5 6.7z" /></>,

  // States and marks
  create: <path d="M8 3.5v9M3.5 8h9" />,
  back: <path d="M10 3.5 5.5 8l4.5 4.5" />,
}

export const Icon: React.FC<{ kind: string; className?: string }> = ({ kind, className }) => {
  const d = PATHS[kind]
  if (!d) return null
  return (
    <svg className={`sf-icon ${className ?? ""}`} viewBox="0 0 16 16" width="16" height="16"
         fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true" focusable="false">
      {d}
    </svg>
  )
}

/** True when there is a drawing for this kind, so callers can fall back to the word alone. */
export const hasIcon = (kind: string): boolean => kind in PATHS
